import React, {useEffect, useMemo, useState} from "react";
import {GetClassScheduleRequest, IScheduledClassProto} from "../../../compiled";
import '../BookingOverlay.css';
import {longDateFormat, shortTimeFormat} from "../../../util/DateTimeUtil";
import {useGetClassSchedule} from "../../../util/RpcHooks/GetClassSchedule";
import moment from "moment";
import {ActionButton} from "../../../util/UI/ActionButton";
import {WeekAdjuster} from "../WeekAdjuster";
import {LoadingDots} from "../../../util/UI/LoadingDots";

interface Props {
  businessId: string;
  version: string | null | undefined;
  onSelected: (class_: IScheduledClassProto) => void;
}

const spotsRemainingDiv = (scheduledClass: IScheduledClassProto) => {
  if (!scheduledClass) {
    return;
  }

  if (scheduledClass.spotsRemaining as number == 1) {
    return <div className={'InputHelpText'} style={{paddingTop: 2}}>
      only 1 spot remaining!
    </div>
  }
  
  if (scheduledClass.spotsRemaining as number <= 3) {
    return <div className={'InputHelpText'} style={{paddingTop: 2}}>
      only {scheduledClass.spotsRemaining} spots remaining!
    </div>
  }
  
  return;
}

export const ClassSchedule = (props: Props) => {

  const classScheduleRpc = useGetClassSchedule();

  // N weeks in the future; shouldn't be less than 0 or greater than say, 4.
  const [week, setWeek] = useState(0);

  useEffect(() => {
    // We do not actually require authentication at this point, but if the user
    // is already authenticated we allow it because the RPC response will filter
    // out classes the user is already signed up for.
    classScheduleRpc.send(new GetClassScheduleRequest({
      businessId: props.businessId,
      profileVersion: props.version,
      fromTimestampMs: getStartTimestamp(),
      toTimestampMs: getEndTimestamp()
    }))
  }, [week]);

  const getStartTimestamp = () => {
    return moment().startOf('day').add(week, 'weeks').valueOf();
  }

  const getEndTimestamp = () => {
    return moment().endOf('day').add(week, 'weeks').add(6, 'days').valueOf();
  }

  const groupByDateString = (classes: IScheduledClassProto[]) => {

    const dict = new Map<string, IScheduledClassProto[]>;

    // Don't mutate the passed in array.
    let sortedClasses = [...classes];
    sortedClasses.sort((a, b) => (a?.startTimestamp as number) - (b?.startTimestamp as number));

    let startIndex = 0;

    for (let i = 0; i < sortedClasses.length; i++) {

      let previousDateString = new Date(sortedClasses[startIndex].startTimestamp as number).toDateString();
      let thisDateString = new Date(sortedClasses[i].startTimestamp as number).toDateString();

      if (thisDateString != previousDateString) {

        // This position in the array has a different date than the last position
        // in the array. Add a new subarray to the return value.
        // Note this is a shallow copy which is very good!
        dict.set(previousDateString, sortedClasses.slice(startIndex, i));
        startIndex = i;
      }
    }

    if (startIndex < sortedClasses.length) {

      // Clean up the last one.
      dict.set(new Date(sortedClasses[startIndex].startTimestamp as number).toDateString(),
          sortedClasses.slice(startIndex))
    }

    return dict;
  }

  const groupedClasses = useMemo(() => {
    return groupByDateString(classScheduleRpc?.response?.classes ?? []);
  }, [classScheduleRpc?.response?.classes]);

  const HeaderRow = (props: { dateString: string }) => {
    return <tr>
      <th colSpan={5} className={'ClassScheduleHeader'}>
        {longDateFormat(new Date(props.dateString))}
      </th>
    </tr>
  }

  const ButtonContent = (props: { scheduledClass: IScheduledClassProto, onSelected: () => void }) => {

    if (props.scheduledClass.isUserInClass) {
      return <div>You're signed up for this class.</div>
    }

    const now = new Date();
    if (now.getTime() >= (props.scheduledClass.startTimestamp as number)) {
      return <div>Class has started</div>
    }

    if ((props.scheduledClass.spotsRemaining as number <= 0)) {
      return <div><b>This class is full!</b></div>
    }

    return <div className={''} style={{gap: 3}}>
      <ActionButton onClick={props.onSelected}>Book now</ActionButton>
      {spotsRemainingDiv(props.scheduledClass)}
    </div>
  }
  
  const ClassRow = (props: { scheduledClass: IScheduledClassProto, onSelected: () => void }) => {

    let startTime = new Date(props.scheduledClass.startTimestamp as number);
    let endTime = new Date(props.scheduledClass.endTimestamp as number);

    return <tr>
      <td><b>{shortTimeFormat(startTime)} - {shortTimeFormat(endTime)}</b></td>
      <td><b>{props.scheduledClass.className}</b></td>
      <td>{props.scheduledClass.teacherName}</td>
      <td>{props.scheduledClass.locationName}</td>
      <td><ButtonContent scheduledClass={props.scheduledClass} onSelected={props.onSelected}/></td>
    </tr>
  }

  return (<>

    <WeekAdjuster week={week} onWeekChange={setWeek}/>
    
    <table className={'ClassScheduleTable'}>
      {Array.from(groupedClasses.keys()).sort((a, b) => {
        // Awkwardly the groupedClasses map is in datestring.
        console.log('comparing', a, b, Date.parse(a), Date.parse(b))
        return Date.parse(a) - Date.parse(b)
      }).map(dateString => {
        return <>
          <HeaderRow dateString={dateString}/>
          {groupedClasses.get(dateString)?.map(class_ => {
            return <ClassRow scheduledClass={class_} onSelected={() => props.onSelected(class_)}/>
          })}
        </>
      })}

      {classScheduleRpc.rpcState.state == 'pending' && <div>Loading classes<LoadingDots/></div>}
      {classScheduleRpc.rpcState.state == 'error' &&
          <div className={'ErrorText'}>{classScheduleRpc.rpcState.message}</div>}
      {classScheduleRpc.rpcState.state == 'success' &&
          !classScheduleRpc.response?.classes?.length &&
          <h3>Sorry, no classes available! Please try a different week.</h3>}

    </table>
  </>);
}